import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";

import type { IGeocodeResult } from "../../../api/mapController";
import type { ISegment } from "../../../helpers/segmentFilterHook";
import { MapController } from "../../../api/mapController";
import { emptySegmentFilters } from "../../../components/segmentFilters";
import { useMapSettings } from "../hooks";

export const MapFiltersContext = createContext<{
	filters: IMapFilter;
	setFilters: (filters: IMapFilter) => void;
}>(null);

export const useEmptyFilters = () => {
	const mapSettingsQuery = useMapSettings();

	const emptyMapFilter: IMapFilter = {
		...emptySegmentFilters,
		type: undefined,
		positions: false,
		leads: false,
		convertedLeads: false,
		retailLocations: false,
		activeOnly: false,
		activeMachineOnly: false,
		activeRepOnly: false,
		startDateTime: startOfDay(new Date()),
		endDateTime: endOfDay(new Date()),
		reps: [],
		machines: [],
		poi: undefined,
		enrollmentId: undefined,
		limitToLastHours: 24,
		showLines: false,
		lastPositions: "",
		showSales: false,
		goodSales: false,
		phoneCarrierType: "",
	};

	return useMemo(() => {
		return {
			...emptyMapFilter,
			leads: mapSettingsQuery.data?.showLeads,
			positions: mapSettingsQuery.data?.showPositions,
			showSales: mapSettingsQuery.data?.showSales,
			retailLocations: mapSettingsQuery.data?.showRetailLocations,
			lastPositions: mapSettingsQuery.data?.defaultMaxPositions.toString(),
		};
	}, [mapSettingsQuery.data]);
};

export function MapFiltersProvider({
	children,
	startingEntityType,
	startingEntityId,
}: {
	children: ReactNode;
	startingEntityType: string;
	startingEntityId: string;
}) {
	const emptyFilters = useEmptyFilters();
	const [filters, setFilters] = useState<IMapFilter>(() => ({
		...emptyFilters,
	}));

	useEffect(() => {
		if (startingEntityType && startingEntityId) {
			initializeFilters();
		}

		async function initializeFilters() {
			const initialFilters = await MapController.getInitialFilters(startingEntityType, startingEntityId);
			setFilters({ ...filters, ...initialFilters });
		}
	}, [startingEntityType, startingEntityId, filters]);

	useEffect(() => {
		if (!filters.positions) setFilters({ ...filters, showLines: false });
	}, [filters.positions]);

	return <MapFiltersContext.Provider value={{ filters, setFilters }}>{children}</MapFiltersContext.Provider>;
}

export function useMapFilters() {
	const context = useContext(MapFiltersContext);
	if (!context) {
		throw new Error("useMapFilters must be used within a MapFiltersProvider");
	}
	return context;
}

export type MapType = "LiveView" | "GeographicView" | "Representatives" | "Machines" | "Enrollment";

export interface IMapFilter extends ISegment {
	type: MapType | undefined;
	retailLocations: boolean;
	leads: boolean;
	convertedLeads: boolean;
	positions: boolean;
	activeMachineOnly: boolean;
	activeRepOnly: boolean;
	activeOnly: boolean;
	startDateTime: Date;
	endDateTime: Date;
	limitToLastHours: number;
	reps: number[];
	machines: number[];
	poi: IGeocodeResult | undefined;
	enrollmentId: number | undefined;
	showLines: boolean;
	lastPositions: string;
	showSales: boolean;
	goodSales: boolean;
	phoneCarrierType: "" | "landline" | "mobile" | "voip";
}
